import { Close } from "@mui/icons-material";
import {
	Alert,
	AppBar,
	Box,
	Divider,
	FormControl,
	IconButton,
	LinearProgress,
	Modal,
	ToggleButton,
	ToggleButtonGroup,
	Toolbar,
	Tooltip,
	Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useState } from "react";
import {
	Detail,
	getCasesByIds,
	getData,
	getDetails,
	Person,
	SessionState,
} from "../utils";

function getRowData(details: Detail[], resolvedCases: any[], people: Person[]) {
	return details.map((detail) => {
		const associatedCase = resolvedCases.find(
			({ _id }) => _id === detail.caseId
		);
		return {
			id: detail._id,
			caseId: detail.caseId,
			clientName:
				associatedCase?.["Client Name$"] ||
				associatedCase?.["Client Name$_0"],
			assignees: [(detail as any).assignee, ...(detail.assignees || [])]
				.filter((a) => !!a)
				.map((a) => people.find((p) => p._id === a)?.name || a)
				.join(", "),
			deadline: detail.date,
			description: detail.description,
		};
	});
}

function removeDuplicateDetails(details: Detail[]) {
	let newDetails: Detail[] = [];
	details.forEach((detail) => {
		if (!newDetails.find((d) => d._id === detail._id)) {
			newDetails.push(detail);
		}
	});
	return newDetails;
}

interface ProfileProps {
	people: Person[];
	selectedName: string | null;
	sessionState: SessionState;
	setSessionState: (arg0: SessionState) => void;
	setDetailsModal: (arg0: string) => void;
	onClose: () => void;
}

export function Profile({
	people,
	selectedName,
	sessionState,
	setSessionState,
	setDetailsModal,
	onClose,
}: ProfileProps) {
	const [types, setTypes] = useState<string[]>(["task", "deadline"]);

	const casesQuery = useQuery({
		queryKey: ["cases", selectedName],
		queryFn: () =>
			!!selectedName && types.length > 0
				? getData(
						{
							archived: false,
							$and: [
								{
									$or: [
										{
											attorneys: { $all: [selectedName] },
										},
										{
											secondaryAttorneys: {
												$all: [selectedName],
											},
										},
										{
											staffs: { $all: [selectedName] },
										},
										{
											secondaryStaffs: {
												$all: [selectedName],
											},
										},
									],
								},
							],
						},
						sessionState,
						setSessionState
				  )
				: [],
		enabled: true,
		refetchInterval: 60000,
	});

	const today = new Date();
	today.setHours(0, 0, 0, 0);

	const detailsQuery = useQuery({
		queryKey: ["details-tasks-deadlines", selectedName, types],
		queryFn: () =>
			!!selectedName && types.length > 0
				? getDetails(
						{
							archived: false,
							assignees: { $elemMatch: { $eq: selectedName } },
							$or: types.map((type) => ({ type })),
							isComplete: { $in: [false, null] },
						},
						sessionState,
						setSessionState
				  )
				: [],
		enabled: true,
		refetchInterval: 60000,
	});

	const caseIds = (casesQuery.data || []).map(
		(caseValue: any) => caseValue._id
	);

	const detailsFromCasesQuery = useQuery({
		queryKey: ["details-cases-tasks-deadlines", caseIds, types],
		queryFn: () =>
			caseIds
				? getDetails(
						{
							caseId: { $in: caseIds },
							archived: false,
							$or: types.map((type) => ({ type })),
							isComplete: { $in: [false, null] },
						},
						sessionState,
						setSessionState
				  )
				: [],
		enabled: true,
		refetchInterval: 60000,
	});

	const casesToPull = (detailsQuery.data || []).map((d: Detail) => d.caseId);

	const otherCasesQuery = useQuery({
		queryKey: ["other-cases", casesToPull],
		queryFn: () =>
			getCasesByIds(casesToPull, sessionState, setSessionState),
		enabled: true,
	});

	const data: Detail[] = removeDuplicateDetails([
		...(detailsQuery.data || []),
		...(detailsFromCasesQuery.data || []),
	]);
	const tasks = data.filter(({ type }) => type === "task");
	const deadlines = data.filter(({ type }) => type === "deadline");

	const resolvedCases = [
		...(casesQuery.data || []),
		...(otherCasesQuery.data || []),
	];

	const isError = false;
	const isLoading =
		detailsQuery.isLoading ||
		casesQuery.isLoading ||
		detailsFromCasesQuery.isLoading;

	return (
		<Modal open={selectedName !== null} onClose={() => onClose()}>
			<Box
				sx={{
					position: "absolute",
					top: "15px",
					maxHeight: "calc(100% - 30px)",
					left: "30px",
					right: "30px",
					boxShadow: 24,
					background: (theme) => theme.palette.background.paper,
					overflow: "auto",
					borderRadius: "5px",
				}}
			>
				<Box
					sx={{
						minWidth: "800px",
					}}
				>
					<AppBar position="sticky">
						<Toolbar>
							<Typography
								variant="h6"
								color="inherit"
								component="div"
								sx={{ flex: 1 }}
							>
								{
									people.find((p) => p._id === selectedName)
										?.name
								}
							</Typography>
							<Tooltip title="Close Window">
								<IconButton
									onClick={() => onClose()}
									sx={{ color: "inherit" }}
								>
									<Close />
								</IconButton>
							</Tooltip>
						</Toolbar>
						{detailsQuery.isFetching && (
							<LinearProgress
								sx={{
									position: "absolute",
									width: "100%",
									bottom: "-4px",
								}}
							/>
						)}
						{isError && (
							<Alert variant="filled" severity="error">
								An error occurred, try refreshing the page.
							</Alert>
						)}
					</AppBar>
					<Box sx={{ m: 1 }}>
						<Box
							sx={{
								position: "relative",
								width: "100%",
							}}
						>
							<Box>
								<FormControl sx={{ m: 1 }}>
									<ToggleButtonGroup
										value={types}
										onChange={(_, newTypes) =>
											setTypes(newTypes)
										}
									>
										<ToggleButton value="task">
											Tasks
										</ToggleButton>
										<ToggleButton value="deadline">
											Deadlines
										</ToggleButton>
									</ToggleButtonGroup>
								</FormControl>
							</Box>
						</Box>
						<Divider />
						<Box
							sx={{
								position: "relative",
								width: "100%",
							}}
						>
							{types.includes("task") && (
								<Box
									sx={{
										height: 350,
										m: 1,
										marginBottom: "64px",
									}}
								>
									<Typography
										variant="h6"
										color={"white"}
										sx={{ m: 1 }}
									>
										Tasks
									</Typography>
									<DataGrid
										sx={{
											"&.MuiDataGrid-root .MuiDataGrid-cell:focus-within":
												{
													outline: "none !important",
												},
										}}
										initialState={{
											sorting: {
												sortModel: [
													{
														field: "deadline",
														sort: "desc",
													},
												],
											},
											pagination: {
												paginationModel: {
													pageSize: 5,
												},
											},
										}}
										pageSizeOptions={[5, 10, 25]}
										disableColumnMenu
										loading={isLoading}
										onRowClick={(params) =>
											setDetailsModal(params.row.caseId)
										}
										columns={[
											{
												field: "clientName",
												headerName: "Client Name",
												flex: 1,
												filterable: false,
												hideable: false,
												renderCell: (params) =>
													params.value === "" ||
													params.value ===
														undefined ? (
														<span
															style={{
																color: "grey",
															}}
														>
															N/A
														</span>
													) : (
														params.value
													),
											},
											{
												field: "assignees",
												headerName: "Assignee Name(s)",
												flex: 1,
												filterable: false,
												hideable: false,
												renderCell: (params) =>
													params.value === "" ||
													params.value ===
														undefined ? (
														<span
															style={{
																color: "grey",
															}}
														>
															N/A
														</span>
													) : (
														params.value
													),
											},
											{
												field: "deadline",
												flex: 1,
												headerName: "Task Due",
												filterable: false,
												hideable: false,
												sortComparator: (a, b) => {
													if (
														a === undefined &&
														b === undefined
													) {
														return 0;
													}
													if (a === undefined) {
														return -1;
													}
													if (b === undefined) {
														return 1;
													}
													return dayjs(b).diff(
														dayjs(a)
													);
												},
												renderCell: (params) => {
													const dayDiff = dayjs(
														params.value
													).diff(dayjs(), "days");
													return (
														<span
															style={{
																color:
																	dayDiff < 7
																		? "red"
																		: "inherit",
															}}
														>
															{dayDiff < 0
																? "(Overdue) "
																: ""}
															{dayjs(
																params.value
															).format(
																"MM/DD/YYYY hh:mm A"
															)}
														</span>
													);
												},
											},
											{
												field: "description",
												headerName: "Description",
												flex: 2,
												filterable: false,
												hideable: false,
											},
										]}
										rows={getRowData(
											tasks,
											resolvedCases,
											people
										)}
									/>
								</Box>
							)}
							{types.includes("deadline") && (
								<Box sx={{ height: 350, m: 1 }}>
									<Typography
										variant="h6"
										color={"white"}
										sx={{ m: 1 }}
									>
										Deadlines
									</Typography>
									<DataGrid
										sx={{
											"&.MuiDataGrid-root .MuiDataGrid-cell:focus-within":
												{
													outline: "none !important",
												},
										}}
										initialState={{
											sorting: {
												sortModel: [
													{
														field: "deadline",
														sort: "desc",
													},
												],
											},
											pagination: {
												paginationModel: {
													pageSize: 5,
												},
											},
										}}
										pageSizeOptions={[5, 10, 25]}
										disableColumnMenu
										loading={isLoading}
										onRowClick={(params) =>
											setDetailsModal(params.row.caseId)
										}
										columns={[
											{
												field: "clientName",
												headerName: "Client Name",
												flex: 1,
												filterable: false,
												hideable: false,
												renderCell: (params) =>
													params.value === "" ||
													params.value ===
														undefined ? (
														<span
															style={{
																color: "grey",
															}}
														>
															N/A
														</span>
													) : (
														params.value
													),
											},
											{
												field: "assignees",
												headerName: "Assignee Name(s)",
												flex: 1,
												filterable: false,
												hideable: false,
												renderCell: (params) =>
													params.value === "" ||
													params.value ===
														undefined ? (
														<span
															style={{
																color: "grey",
															}}
														>
															N/A
														</span>
													) : (
														params.value
													),
											},
											{
												field: "deadline",
												flex: 1,
												headerName: "Deadline Due",
												filterable: false,
												hideable: false,
												sortComparator: (a, b) => {
													if (
														a === undefined &&
														b === undefined
													) {
														return 0;
													}
													if (a === undefined) {
														return -1;
													}
													if (b === undefined) {
														return 1;
													}
													return dayjs(b).diff(
														dayjs(a)
													);
												},
												renderCell: (params) => {
													const dayDiff = dayjs(
														params.value
													).diff(dayjs(), "days");
													return (
														<span
															style={{
																color:
																	dayDiff < 7
																		? "red"
																		: "inherit",
															}}
														>
															{dayDiff < 0
																? "(Overdue) "
																: ""}
															{dayjs(
																params.value
															).format(
																"MM/DD/YYYY hh:mm A"
															)}
														</span>
													);
												},
											},
											{
												field: "description",
												headerName: "Description",
												flex: 2,
												filterable: false,
												hideable: false,
											},
										]}
										rows={getRowData(
											deadlines,
											resolvedCases,
											people
										)}
									/>
								</Box>
							)}
						</Box>
					</Box>
				</Box>
			</Box>
		</Modal>
	);
}
